import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  CircularProgress,
  Alert,
  Snackbar,
  Stack,
  SelectChangeEvent
} from '@mui/material';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { supabase } from '../../services/supabase';
import type { Game, League } from '../../types/models';

const ExportData: React.FC = () => {
  const [leagues, setLeagues] = useState<League[]>([]);
  const [selectedLeagueId, setSelectedLeagueId] = useState<string>('');
  const [games, setGames] = useState<Game[]>([]);
  const [selectedGames, setSelectedGames] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<boolean>(false);
  const [exporting, setExporting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning'
  });

  // Fetch leagues when component mounts
  useEffect(() => {
    fetchLeagues();
  }, []);

  // Fetch games when league is selected
  useEffect(() => {
    if (selectedLeagueId) {
      fetchGames(selectedLeagueId);
    } else {
      // Clear games if no league is selected
      setGames([]);
      setSelectedGames(new Set());
    }
  }, [selectedLeagueId]);

  const fetchLeagues = async () => {
    try {
      setLoading(true);
      
      // Import the helpers we need
      const { fixTypes, getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      
      // Start with base query
      let baseQuery = supabase
        .from('leagues')
        .select(`
          id,
          name,
          judge_id,
          created_at,
          club:club_id (
            id,
            name
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `);
      
      // Check if user is a superadmin
      const superAdmin = await isSuperAdmin();
      let queryResult;
      
      if (superAdmin) {
        // Superadmins see all leagues
        queryResult = await baseQuery.order('created_at', { ascending: false });
      } else {
        // Regular admins only see leagues from their club
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          queryResult = { data: [], error: null };
        } else {
          // Filter leagues by the admin's club
          queryResult = await baseQuery
            .eq('club_id', userRole.club_id)
            .order('created_at', { ascending: false });
        }
      }
      
      const { data, error } = queryResult;
      
      if (error) throw error;
      
      // Fix types and set leagues
      const typedData = fixTypes<League>(data || []);
      setLeagues(typedData);
      
      // Set the default league to the most recently created one (first in the list)
      if (typedData.length > 0) {
        setSelectedLeagueId(typedData[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
      setError('Failed to load leagues: ' + error.message);
      showSnackbar('Failed to load leagues', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchGames = async (leagueId: string) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('games')
        .select(`
          id,
          session_num,
          number_in_session,
          occurred_at,
          winner,
          video_link,
          judge:judge_id (
            id,
            nickname
          )
        `)
        .eq('league_id', leagueId)
        .order('session_num', { ascending: false })
        .order('number_in_session', { ascending: false });
      
      if (error) throw error;
      
      // Fix types
      const { fixTypes } = await import('../../services/adminPermissions');
      const typedData = fixTypes<Game>(data || []);
      setGames(typedData);
      
      // Select the latest 5 games by default (or all games if less than 5)
      const initialSelection = new Set<string>();
      typedData.slice(0, 5).forEach(game => initialSelection.add(game.id));
      setSelectedGames(initialSelection);
    } catch (error: any) {
      console.error('Error fetching games:', error.message);
      setError('Failed to load games: ' + error.message);
      showSnackbar('Failed to load games', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLeagueChange = (event: SelectChangeEvent) => {
    setSelectedLeagueId(event.target.value);
  };

  const handleToggleSelectGame = (gameId: string) => {
    const newSelectedGames = new Set(selectedGames);
    if (newSelectedGames.has(gameId)) {
      newSelectedGames.delete(gameId);
    } else {
      newSelectedGames.add(gameId);
    }
    setSelectedGames(newSelectedGames);
  };
  
  const handleSelectAllGames = () => {
    // Select all games
    const allGameIds = games.map(game => game.id);
    setSelectedGames(new Set(allGameIds));
  };
  
  const handleDeselectAllGames = () => {
    // Clear all selections
    setSelectedGames(new Set());
  };

  const handleExportJson = async () => {
    if (selectedGames.size === 0) {
      showSnackbar('Please select at least one game to export', 'warning');
      return;
    }

    try {
      setExporting(true);
      
      // Fetch complete data for selected games
      const selectedGameIds = Array.from(selectedGames);
      
      // Fetch games with league info
      const { data: gamesData, error: gamesError } = await supabase
        .from('games')
        .select(`
          id,
          session_num,
          number_in_session,
          occurred_at,
          winner,
          video_link,
          league:league_id (
            id,
            name,
            club:club_id (
              id,
              name
            )
          ),
          judge:judge_id (
            id,
            nickname,
            full_name
          )
        `)
        .in('id', selectedGameIds);
      
      if (gamesError) throw gamesError;
      
      // Fetch game results with player info for the selected games
      const { data: resultsData, error: resultsError } = await supabase
        .from('game_results')
        .select(`
          id,
          game_id,
          player_id,
          is_win,
          bonus_points,
          role,
          is_killed_first,
          winning_move,
          comment,
          player_num,
          player:player_id (
            id,
            nickname,
            full_name
          )
        `)
        .in('game_id', selectedGameIds);
      
      if (resultsError) throw resultsError;
      
      // Organize the data - map results to their respective games
      const exportData = gamesData.map(game => {
        const gameResults = resultsData.filter(result => result.game_id === game.id);
        return {
          ...game,
          results: gameResults
        };
      });
      
      // Create JSON file and trigger download
      const fileName = `mafia-export-${new Date().toISOString().split('T')[0]}.json`;
      const jsonStr = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonStr], { type: 'application/json' });
      
      // Create download link and trigger click
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showSnackbar(`Successfully exported ${selectedGames.size} games`, 'success');
    } catch (error: any) {
      console.error('Error exporting data:', error);
      setError('Failed to export data: ' + error.message);
      showSnackbar('Failed to export data', 'error');
    } finally {
      setExporting(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format game number for display (Session X, Game Y)
  const formatGameNumber = (game: Game) => {
    return `Session ${game.session_num}, Game ${game.number_in_session}`;
  };

  return (
    <Box>
      <Typography variant="h6" component="h2" gutterBottom>
        Export Game Data
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack spacing={3}>
          <Typography variant="body1">
            Select a league and games to export data in JSON format.
          </Typography>
          
          <FormControl fullWidth>
            <InputLabel>League</InputLabel>
            <Select
              value={selectedLeagueId}
              onChange={handleLeagueChange}
              label="League"
              disabled={loading}
            >
              {leagues.length === 0 ? (
                <MenuItem disabled value="">
                  No leagues available
                </MenuItem>
              ) : (
                leagues.map(league => (
                  <MenuItem key={league.id} value={league.id}>
                    {league.name} ({league.club?.name || 'Unknown Club'})
                    - Judge: {league.judge?.nickname || 'Unknown'}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
          
          {selectedLeagueId && (
            <>
              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
                  <Typography variant="subtitle2">
                    Selected: {selectedGames.size} / {games.length} games
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={handleSelectAllGames}
                      disabled={games.length === 0 || loading}
                    >
                      Select All
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined"
                      onClick={handleDeselectAllGames}
                      disabled={selectedGames.size === 0 || loading}
                    >
                      Deselect All
                    </Button>
                  </Box>
                </Box>
                
                <TableContainer component={Paper} sx={{ maxHeight: 300, overflow: 'auto' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" width="5%"></TableCell>
                        <TableCell width="25%">Game</TableCell>
                        <TableCell width="20%">Date</TableCell>
                        <TableCell width="15%">Winner</TableCell>
                        <TableCell width="35%">Judge</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                            <CircularProgress size={24} />
                          </TableCell>
                        </TableRow>
                      ) : games.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                            No games found for this league
                          </TableCell>
                        </TableRow>
                      ) : (
                        games.map(game => (
                          <TableRow 
                            key={game.id}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleToggleSelectGame(game.id)}
                          >
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedGames.has(game.id)}
                                onChange={() => handleToggleSelectGame(game.id)}
                                onClick={(e) => e.stopPropagation()} // Prevent double toggle
                                disabled={loading || exporting}
                              />
                            </TableCell>
                            <TableCell>{formatGameNumber(game)}</TableCell>
                            <TableCell>{new Date(game.occurred_at).toLocaleDateString()}</TableCell>
                            <TableCell>{game.winner}</TableCell>
                            <TableCell>{game.judge?.nickname || 'Unknown'}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                
                <Button 
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportJson}
                  disabled={selectedGames.size === 0 || loading || exporting}
                  sx={{ mt: 2 }}
                >
                  {exporting ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={20} color="inherit" />
                      <span>Exporting...</span>
                    </Box>
                  ) : (
                    `Export ${selectedGames.size} Games as JSON`
                  )}
                </Button>
              </Box>
            </>
          )}
        </Stack>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ExportData;